import { useState } from 'react';

import { useContractParties, useLinkParty, useShows, useUpdateContract } from '../../api/hooks';
import type { ContractView } from '../../api/types';
import {
  Badge,
  Button,
  FormField,
  Modal,
  SelectWithCaption,
  TextInput,
  useToast,
} from '../../components/DesignSystem';
import { ADVANCE_MODES, findAdvanceMode } from '../../lib/advanceModes';
import { CURRENCIES } from '../../lib/currencies';
import { errorMessage } from '../../lib/errors';
import { findGoverningPolicy, GOVERNING_POLICIES } from '../../lib/governingPolicies';
import { seasonSlug, showSlug } from '../../lib/slug';
import { EMPTY_PARTY_VALUE, PartyField, usePartyResolver } from '../upload/PartyField';
import type { PartyFieldValue } from '../upload/PartyField';

interface EditApprovedTermModalProps {
  contract: ContractView;
  showName: string;
  seasonName: string;
  onClose: () => void;
}

/** Post-approval editing of a contract's reporting configuration and parties. */
export function EditApprovedTermModal({
  contract,
  showName,
  seasonName,
  onClose,
}: EditApprovedTermModalProps) {
  const { showToast } = useToast();
  const shows = useShows({ limit: 100 });
  const updateContract = useUpdateContract();
  const parties = useContractParties(contract.id);
  const linkParty = useLinkParty();
  const resolveParty = usePartyResolver();

  const [reportedCurrency, setReportedCurrency] = useState(contract.reportedCurrency);
  const [governingPolicy, setGoverningPolicy] = useState(contract.governingPolicy ?? '');
  const [advanceMode, setAdvanceMode] = useState(findAdvanceMode(contract.advanceMode)?.key ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [newParty, setNewParty] = useState<PartyFieldValue>(EMPTY_PARTY_VALUE);
  const [newPartyRole, setNewPartyRole] = useState('');

  const show = shows.data?.data.find((entry) => entry.id === contract.showId);
  const season = show?.seasons.find((entry) => entry.id === contract.seasonId);
  const policy = findGoverningPolicy(governingPolicy);

  async function handleSave() {
    setIsSaving(true);
    try {
      await updateContract.mutateAsync({
        contractId: contract.id,
        input: {
          reportedCurrency,
          governingPolicy: governingPolicy === '' ? undefined : governingPolicy,
          advanceMode: advanceMode === '' ? undefined : advanceMode,
        },
      });
      showToast('Contract updated');
      onClose();
    } catch (error) {
      showToast(errorMessage(error, 'Could not update contract'), 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddParty() {
    if (newParty.choice === '') {
      showToast('Choose or name a party first', 'error');
      return;
    }

    try {
      const partyId = await resolveParty(newParty);
      if (partyId === undefined) {
        return;
      }
      await linkParty.mutateAsync({
        contractId: contract.id,
        partyId,
        ...(newPartyRole.trim() === '' ? {} : { role: newPartyRole.trim() }),
      });
      showToast('Party added');
      setNewParty(EMPTY_PARTY_VALUE);
      setNewPartyRole('');
    } catch (error) {
      showToast(errorMessage(error, 'Could not add party'), 'error');
    }
  }

  return (
    <Modal
      actions={
        <>
          <Button disabled={isSaving} onClick={handleSave} type="button">
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
          <Button onClick={onClose} type="button" variant="secondary">
            Cancel
          </Button>
        </>
      }
      title="Edit Approved Term"
    >
      <div className="rfp-form-grid">
        <FormField htmlFor="edit-term-parent-id" label="Parent ID">
          <TextInput disabled id="edit-term-parent-id" value={showSlug(showName)} />
        </FormField>
        <FormField htmlFor="edit-term-show-name" label="Show Name">
          <TextInput disabled id="edit-term-show-name" value={showName} />
        </FormField>
        <FormField htmlFor="edit-term-season-id" label="Season ID">
          <TextInput
            disabled
            id="edit-term-season-id"
            value={
              season
                ? seasonSlug(showName, season.seasonCode, season.name)
                : seasonSlug(showName, seasonName)
            }
          />
        </FormField>
        <FormField htmlFor="edit-term-season-number" label="Season Number">
          <TextInput
            disabled
            id="edit-term-season-number"
            value={season ? String(season.number) : '—'}
          />
        </FormField>
        <FormField htmlFor="edit-term-season-name" label="Season Name">
          <TextInput disabled id="edit-term-season-name" value={season?.name ?? '—'} />
        </FormField>
      </div>

      <div className="rfp-form-grid" style={{ marginTop: 16 }}>
        <SelectWithCaption
          caption="Currency for reports, advances, and SUA deductions."
          htmlFor="edit-term-currency"
          label="Reported Currency"
          onChange={setReportedCurrency}
          value={reportedCurrency}
        >
          {CURRENCIES.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.label}
            </option>
          ))}
        </SelectWithCaption>

        <SelectWithCaption
          caption={
            policy
              ? `Performer ${policy.performerRate.toFixed(2)}% / Writer ${policy.writerRate.toFixed(2)}% (${policy.territory} only)`
              : 'No default residual policy selected.'
          }
          htmlFor="edit-term-governing-policy"
          label="Governing Policy (Residuals)"
          onChange={setGoverningPolicy}
          value={governingPolicy}
        >
          <option value="">—</option>
          {GOVERNING_POLICIES.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </SelectWithCaption>

        <SelectWithCaption
          caption={findAdvanceMode(advanceMode)?.caption ?? ''}
          htmlFor="edit-term-advance-mode"
          label="Advance Mode"
          onChange={setAdvanceMode}
          value={advanceMode}
        >
          <option value="">—</option>
          {ADVANCE_MODES.map((mode) => (
            <option key={mode.key} value={mode.key}>
              {mode.label}
            </option>
          ))}
        </SelectWithCaption>
      </div>

      <div style={{ marginTop: 16 }}>
        <p className="rfp-card__title" style={{ fontSize: '0.95rem' }}>
          Residual Defaults (applied to new parties)
        </p>
        {policy ? (
          <div className="rfp-row" style={{ marginTop: 8 }}>
            <Badge tone="teal">Writer Residuals (WGGB — {policy.writerRate.toFixed(2)}%)</Badge>
            <Badge tone="teal">
              Performer Residuals (PAC — {policy.performerRate.toFixed(2)}%)
            </Badge>
          </div>
        ) : (
          <p className="rfp-muted-text">No governing policy selected.</p>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <p className="rfp-card__title" style={{ fontSize: '0.95rem' }}>
          Parties
        </p>
        {parties.isLoading ? (
          <p className="rfp-loading">Loading parties…</p>
        ) : (parties.data ?? []).length === 0 ? (
          <p className="rfp-muted-text">
            No parties added yet. Click "+Add Party" to add a participant.
          </p>
        ) : (
          <ul className="rfp-stack" style={{ gap: 8 }}>
            {(parties.data ?? []).map((party) => (
              <li className="rfp-mono-text" key={party.id}>
                {party.name}
                {party.role ? ` · ${party.role}` : ''}
              </li>
            ))}
          </ul>
        )}

        <div className="rfp-row" style={{ marginTop: 12 }}>
          <PartyField id="edit-term-new-party" onChange={setNewParty} value={newParty} />
          <FormField htmlFor="edit-term-new-party-role" label="Role (optional)">
            <TextInput
              id="edit-term-new-party-role"
              onChange={(event) => setNewPartyRole(event.target.value)}
              placeholder="e.g. Producer"
              value={newPartyRole}
            />
          </FormField>
          <Button disabled={linkParty.isPending} onClick={handleAddParty} type="button">
            + Add Party
          </Button>
        </div>
      </div>
    </Modal>
  );
}
